import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Renders markdown content with safety restrictions.
 * - Supports GFM (GitHub Flavored Markdown): bold, italic, strikethrough, lists, blockquotes, links
 * - Limits headers to h4-h6 (h1-h3 get downgraded to h4)
 * - Opens links in new tabs
 * - Does NOT support raw HTML or images (use upload system instead)
 */
export const MarkdownRenderer = ({ content, className = '' }) => {
  if (!content) return null;

  return (
    <div className={`prose prose-sm max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
        // Downgrade large headers to h4 to prevent visual hierarchy issues
        h1: ({ node, ...props }) => (
          <h4 className="text-lg font-semibold mt-4 mb-2" {...props} />
        ),
        h2: ({ node, ...props }) => (
          <h4 className="text-lg font-semibold mt-4 mb-2" {...props} />
        ),
        h3: ({ node, ...props }) => (
          <h4 className="text-base font-semibold mt-3 mb-2" {...props} />
        ),
        h4: ({ node, ...props }) => (
          <h4 className="text-base font-semibold mt-3 mb-2" {...props} />
        ),
        h5: ({ node, ...props }) => (
          <h5 className="text-sm font-semibold mt-2 mb-1" {...props} />
        ),
        h6: ({ node, ...props }) => (
          <h6 className="text-sm font-medium mt-2 mb-1" {...props} />
        ),
        // Open links in new tabs
        a: ({ node, ...props }) => (
          <a
            className="text-primary-600 hover:text-primary-700 underline"
            target="_blank"
            rel="noopener noreferrer"
            {...props}
          />
        ),
        // Style blockquotes
        blockquote: ({ node, ...props }) => (
          <blockquote
            className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-2"
            {...props}
          />
        ),
        // Style inline code (react-markdown v10+ doesn't pass inline prop)
        code: ({ node, className, children, ...props }) => {
          // Check if this is a code block (has language class) vs inline code
          const isCodeBlock = /language-\w+/.test(className || '');
          if (isCodeBlock) {
            return (
              <code
                className={`block bg-gray-100 rounded p-2 text-sm font-mono overflow-x-auto my-2 ${className || ''}`}
                {...props}
              >
                {children}
              </code>
            );
          }
          return (
            <code
              className="bg-gray-100 rounded px-1 py-0.5 text-sm font-mono"
              {...props}
            >
              {children}
            </code>
          );
        },
        // Style code blocks wrapped in pre
        pre: ({ node, children, ...props }) => (
          <pre className="my-2" {...props}>
            {children}
          </pre>
        ),
        // Style lists
        ul: ({ node, ...props }) => (
          <ul className="list-disc list-inside my-2 space-y-1" {...props} />
        ),
        ol: ({ node, ...props }) => (
          <ol className="list-decimal list-inside my-2 space-y-1" {...props} />
        ),
        // Style paragraphs
        p: ({ node, ...props }) => (
          <p className="my-2" {...props} />
        ),
        // Block images - use upload system instead
        img: () => null,
      }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
